import { firstNumber } from './chatArtifactValueUtils';

const CORE_STATUSES = new Set(['ready', 'empty', 'partial', 'failed', 'legacy']);

function count(value) {
  const parsed = firstNumber(value);
  return parsed === undefined ? undefined : Math.max(0, parsed);
}

function countLabel(value) {
  return value.toLocaleString();
}

const FAILURE_PRESENTATION = {
  frozen_scope_pending: {
    heading: 'Core result set is being prepared',
    explanation: 'The frozen result scope is still being prepared.',
    statusMessage: 'Still working…',
    retryable: true,
  },
  frozen_scope_expired: {
    heading: 'Core result set unavailable',
    explanation: 'The frozen result scope is no longer available.',
    statusMessage: "We couldn't load these results. Run the search again.",
  },
  frozen_scope_materialization_failed: {
    heading: 'Core result set unavailable',
    explanation: 'The frozen result scope could not be created.',
    statusMessage: "We couldn't load these results. Run the search again.",
  },
  frozen_scope_invalid: {
    heading: 'Core result set unavailable',
    explanation: 'The frozen result scope is inconsistent or incomplete.',
    statusMessage: "We couldn't load these results. Run the search again.",
  },
};

function failureModel(raw) {
  const error = raw?.error || raw?.detail || {};
  const presentation = FAILURE_PRESENTATION[error.code];
  const forbidden = Boolean(raw?.detail);
  const pending = error.code === 'frozen_scope_pending';
  const retrievedRowCount = count(raw?.eligible_row_count);
  const completeRowCount = count(raw?.complete_row_count);
  const estimatedMatchCount = count(raw?.estimated_total_matches);
  const providerPartial = raw?.provider_partial === true;
  return {
    status: forbidden ? 'forbidden' : pending ? 'pending' : 'failed',
    heading: presentation?.heading || 'Core result set unavailable',
    explanation: presentation?.explanation || 'The Core result set could not be loaded.',
    statusMessage: presentation?.statusMessage || 'Core could not return a result set.',
    recoveryGuidance: presentation?.recoveryGuidance || null,
    errorCode: error.code,
    retryable: presentation?.retryable === true && error.retryable === true,
    providerPartial,
    providerPartialMessage: providerPartial
      ? 'Some providers returned partial results before the Core result set became unavailable.'
      : null,
    samplingMethod: 'unavailable',
    displayedCompanyCount: 0,
    retrievedRowCount,
    completeRowCount,
    estimatedMatchCount,
    estimatedMatchCountIsLowerBound: raw?.estimated_total_matches_is_lower_bound === true,
    tableLabel: 'Core companies',
  };
}

export function resolveCoreCandidates({
  coreResultSet,
  artifactCandidates,
  messageCandidates,
  legacyCandidates,
  eligibleCount,
  sampleLimit,
}) {
  const candidates = artifactCandidates || messageCandidates || legacyCandidates;
  const eligibleCandidates = !coreResultSet && eligibleCount !== undefined
    ? candidates.slice(0, Math.max(0, eligibleCount))
    : candidates;
  return {
    candidates,
    eligibleCandidates,
    sampleCandidates: coreResultSet
      ? (Array.isArray(coreResultSet.raw?.companies) ? coreResultSet.raw.companies : [])
      : eligibleCandidates.slice(0, sampleLimit),
  };
}

/** FE #748: display-only preview rows (up to 30); scope, leads and actions stay on the sample. */
export function displayPreviewRows(artifactData) {
  const rows = artifactData?.preview_rows;
  return Array.isArray(rows) && rows.length > 0 ? rows : null;
}

export function artifactTableLabel(coreResultSet, sampleCount, totalCount) {
  if (coreResultSet?.model?.tableLabel) return coreResultSet.model.tableLabel;
  if (sampleCount === 0) return 'No sample results';
  if (totalCount !== undefined) return `Sample Preview (${sampleCount} of ${totalCount})`;
  return `Showing ${sampleCount} sample ${sampleCount === 1 ? 'company' : 'companies'}`;
}

export function coreResultSetFromPayload(artifactData, message) {
  const artifactOwnsCore = Object.prototype.hasOwnProperty.call(
    artifactData || {},
    'core_result_set'
  );
  const messageOwnsCore = Object.prototype.hasOwnProperty.call(
    message || {},
    'core_result_set'
  );
  if (!artifactOwnsCore && !messageOwnsCore) return null;
  const raw = artifactOwnsCore ? artifactData.core_result_set : message.core_result_set;
  const model = buildCoreResultSetModel(raw);
  if (!model) return null;
  return {
    raw,
    model,
  };
}

export function buildCoreResultSetModel(raw, { loading = false } = {}) {
  if (loading) {
    return {
      status: 'loading',
      heading: 'Core result set',
      statusMessage: 'Loading Core result set…',
      displayedCompanyCount: 0,
      tableLabel: 'Core companies',
    };
  }
  if (!raw || typeof raw !== 'object') return null;
  if (raw.detail || raw.status === 'failed' || raw.error) return failureModel(raw);

  const companies = Array.isArray(raw.companies) ? raw.companies : [];
  const displayedCompanyCount = companies.length;
  const retrievedRowCount = count(raw.eligible_row_count) ?? 0;
  const completeRowCount = count(raw.complete_row_count) ?? 0;
  const estimatedMatchCount = count(raw.estimated_total_matches);
  const samplingMethod = raw?.sampling?.method || 'unavailable';
  const sampledFromCount = count(raw?.sampling?.sampled_from_count) ?? completeRowCount;
  const representative = samplingMethod === 'rank_stratified';
  const legacy = raw.status === 'legacy';

  let statusMessage = null;
  if (raw.status === 'empty') {
    statusMessage = "We couldn't find companies matching your request.";
  } else if (legacy) {
    statusMessage = 'This is a legacy result without a frozen scope.';
  }
  const providerPartial = raw.provider_partial === true;

  return {
    status: CORE_STATUSES.has(raw.status) ? raw.status : 'legacy',
    heading: representative ? 'Core representative sample' : 'Core result set',
    explanation: null,
    statusMessage,
    errorCode: null,
    retryable: false,
    samplingMethod,
    sampledFromCount,
    representative,
    legacy,
    providerPartial,
    providerPartialMessage: providerPartial
      ? 'Some providers returned partial results, so the stored Core result set may contain fewer rows than retrieval found.'
      : null,
    displayedCompanyCount,
    retrievedRowCount,
    completeRowCount,
    estimatedMatchCount,
    estimatedMatchCountIsLowerBound: raw.estimated_total_matches_is_lower_bound === true,
    // The saved total (what Export downloads), so a 5-row sample is not read as
    // the whole list (sweep D3).
    totalLabel: raw.status !== 'empty' && completeRowCount > 0
      ? `Total results: ${countLabel(completeRowCount)} ${completeRowCount === 1 ? 'company' : 'companies'}`
      : null,
    estimatedMatchesLabel:
      `Estimated matches: ${
        estimatedMatchCount === undefined
          ? 'unavailable'
          : `${raw.estimated_total_matches_is_lower_bound === true ? 'at least ' : ''}${countLabel(estimatedMatchCount)}`
      }`,
    tableLabel: representative
      ? `Companies in the Core representative sample (${displayedCompanyCount.toLocaleString()})`
      : `Companies in the Core result set (${displayedCompanyCount.toLocaleString()})`,
  };
}
